/**
 * Unity C# Rendering Helpers
 * Shared utilities for Unity C# code generation
 */

import { TemplateField, NamingConvention } from '@/types/exportTemplates';
import { applyNamingConvention } from '../../core/naming';
import { stripHtmlTags } from '@/lib/utils/documentExportUtils';

/**
 * Get C# type from mapped type string AND actual value
 */
export function getCSharpType(
  mappedType: string,
  actualValue?: any,
  isTranslatable: boolean = false,
  field?: TemplateField,
  blueprints?: any[],
  templateConvention?: NamingConvention
): string {
  // Check if this is a reference field
  if (field?.isReference && field?.referenceType) {
    if (field.referenceType === 'types') {
      return Array.isArray(actualValue) ? 'TypeElement[]' : 'TypeElement';
    }
    if (field.referenceType === 'tags') {
      return Array.isArray(actualValue) ? 'TagElement[]' : 'TagElement';
    }
    if (field.referenceType === 'glossaryItem') {
      // Determine the specific blueprint type for strong typing
      let specificType = 'Template'; // fallback
      
      // PRIORITY 1: Check field.filterOptions (attached during field extraction)
      if (field.filterOptions && field.filterOptions.length === 1 && blueprints && templateConvention) {
        const targetBlueprintId = field.filterOptions[0];
        const targetBlueprint = blueprints.find(bp => bp.id === targetBlueprintId);
        if (targetBlueprint) {
          specificType = applyNamingConvention(targetBlueprint.name || targetBlueprintId, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
        }
      }
      // PRIORITY 2: Fall back to referenceData if filterOptions didn't determine type
      else if (field.referenceData && blueprints && templateConvention) {
        if (Array.isArray(field.referenceData)) {
          // For arrays, check if all items share the same blueprint_id
          const blueprintIds = field.referenceData
            .filter(item => item && item.blueprint_id)
            .map(item => item.blueprint_id);
          
          if (blueprintIds.length > 0) {
            const uniqueBlueprintIds = [...new Set(blueprintIds)];
            if (uniqueBlueprintIds.length === 1) {
              // All items share the same blueprint type
              const sharedBlueprintId = uniqueBlueprintIds[0];
              const refBlueprint = blueprints.find(bp => bp.id === sharedBlueprintId);
              if (refBlueprint) {
                specificType = applyNamingConvention(refBlueprint.name || sharedBlueprintId, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
              }
            }
          }
        } else {
          // Single item reference
          const refItem = field.referenceData;
          if (refItem && refItem.blueprint_id) {
            const refBlueprint = blueprints.find(bp => bp.id === refItem.blueprint_id);
            if (refBlueprint) {
              specificType = applyNamingConvention(refBlueprint.name || refItem.blueprint_id, templateConvention).replace(/[^a-zA-Z0-9_]/g, '_');
            }
          }
        }
      }
      return Array.isArray(actualValue) ? `${specificType}[]` : specificType;
    }
    if (field.referenceType === 'dialog') {
      // Dialog references store dialog keys as strings
      return Array.isArray(actualValue) ? 'string[]' : 'string';
    }
    if (field.referenceType === 'document') {
      // Document fields are exported as arrays of DocumentBlock objects
      return 'DocumentBlock[]';
    }
  }
  
  // If translatable, use TranslationKey instead of string
  if (isTranslatable) {
    if (Array.isArray(actualValue)) return 'TranslationKey[]';
    return 'TranslationKey';
  }
  
  // First check the actual value - if it's an array, return string[]
  if (Array.isArray(actualValue)) return 'string[]';
  
  if (mappedType.includes('bool')) return 'bool';
  if (mappedType.includes('int')) {
    // If the actual value has decimals, use double instead of int
    if (typeof actualValue === 'number' && !Number.isInteger(actualValue)) return 'double';
    return 'int';
  }
  if (mappedType.includes('float')) return 'float';
  if (mappedType.includes('double')) return 'double';
  if (mappedType.includes('[]') || mappedType.includes('Array')) return 'string[]';
  return 'string'; // Default to string
}

/**
 * Format value for C# code output
 */
export function formatValueCSharp(value: any, mappedType: string, field?: TemplateField): string {
  // Handle reference fields - generate object references
  if (field?.isReference && field?.referenceData && field?.referenceType) {
    console.log(`🎯 formatValueCSharp: Reference field detected - type=${field.referenceType}`);
    
    if (field.referenceType === 'types' || field.referenceType === 'tags') {
      // For types and tags, extract the value as a string
      if (Array.isArray(field.referenceData)) {
        // Array of references - extract value from each
        const values = field.referenceData
          .map(refObj => {
            const val = refObj.value || refObj.code || refObj.name || refObj.id;
            return `"${String(val).replace(/"/g, '\\"')}"`;
          })
          .join(', ');
        console.log(`  ✅ Generated array of type/tag values: new[] { ${values} }`);
        return `new[] { ${values} }`;
      } else {
        // Single reference - extract value
        const val = field.referenceData.value || field.referenceData.code || field.referenceData.name || field.referenceData.id;
        const stringValue = `"${String(val).replace(/"/g, '\\"')}"`;
        console.log(`  ✅ Generated type/tag value: ${stringValue}`);
        return stringValue;
      }
    } else if (field.referenceType === 'glossaryItem') {
      // For glossaryItem, add a TODO comment for now
      const itemName = field.referenceData.name || field.referenceData.id || 'Unknown';
      const safeItemName = itemName.replace(/[^a-zA-Z0-9_]/g, '_');
      console.log(`  ⚠️ GlossaryItem reference: ${safeItemName} - using string for now`);
      return `"${safeItemName}" /* TODO: Reference to glossaryItem */`;
    } else if (field.referenceType === 'dialog') {
      // Dialog references - store dialog key as string
      if (Array.isArray(field.referenceData)) {
        const dialogKeys = field.referenceData
          .map(refObj => {
            const dialogKey = refObj.dialog_key || refObj.name || refObj.id;
            return `"${String(dialogKey).replace(/"/g, '\\"')}"`;
          })
          .join(', ');
        console.log(`  ✅ Generated array of dialog keys: new[] { ${dialogKeys} }`);
        return `new[] { ${dialogKeys} }`;
      } else {
        const dialogKey = field.referenceData.dialog_key || field.referenceData.name || field.referenceData.id;
        const stringValue = `"${String(dialogKey).replace(/"/g, '\\"')}"`;
        console.log(`  ✅ Generated dialog key: ${stringValue}`);
        return stringValue;
      }
    } else if (field.referenceType === 'document') {
      // Document references - extract blocks from document data
      const document = field.referenceData;
      if (!document || !document.data) {
        console.log(`  ⚠️ Document has no data, returning empty array`);
        return 'new DocumentBlock[] { }';
      }
      
      // Document data is stored as DocumentData[] (array of sections)
      const blocks: Array<{ type: string; content: string; index: number }> = [];
      let currentIndex = 0;
      
      const documentData = Array.isArray(document.data) ? document.data : [document.data];
      
      documentData.forEach((section: any) => {
        if (section.blocks && Array.isArray(section.blocks)) {
          section.blocks.forEach((block: any) => {
            if (block.type === 'text' && block.content && block.content.trim()) {
              // Strip HTML tags from text content for clean export
              const cleanContent = stripHtmlTags(block.content);
              if (cleanContent.trim()) {
                blocks.push({
                  type: 'text',
                  content: cleanContent,
                  index: currentIndex++
                });
              }
            } else if (block.type === 'image' && block.metadata?.src) {
              blocks.push({
                type: 'image',
                content: block.metadata.src,
                index: currentIndex++
              });
            }
          });
        }
      });
      
      if (blocks.length === 0) {
        console.log(`  ✅ Document has no blocks, returning empty array`);
        return 'new DocumentBlock[] { }';
      }
      
      // Format blocks as C# array
      const blockStrings = blocks.map(block => {
        const escapedContent = block.content
          .replace(/\\/g, '\\\\')
          .replace(/"/g, '\\"')
          .replace(/\n/g, '\\n')
          .replace(/\r/g, '\\r');
        return `new DocumentBlock { type = "${block.type}", content = "${escapedContent}", index = ${block.index} }`;
      });
      
      console.log(`  ✅ Generated ${blocks.length} document blocks`);
      return `new DocumentBlock[] { ${blockStrings.join(', ')} }`;
    }
  }
  
  if (value === null || value === undefined || value === '') {
    if (mappedType.includes('bool')) return 'false';
    if (mappedType.includes('int') || mappedType.includes('float') || mappedType.includes('double')) return '0';
    if (mappedType.includes('[]') || mappedType.includes('Array')) return 'new string[] { }';
    return '""';
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'new string[0]';
    }
    const arrItems = value.map(v => formatValueCSharp(v, 'string')).join(', ');
    return `new[] { ${arrItems} }`;
  }
  
  if (typeof value === 'string') {
    // Escape C# strings
    const escaped = value
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t');
    return `"${escaped}"`;
  }
  
  if (typeof value === 'number') {
    if (mappedType.includes('float')) return `${value}f`;
    // If mapped as int but value has decimals, format as double
    if (mappedType.includes('int') && !Number.isInteger(value)) return value.toString();
    return value.toString();
  }
  
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  
  return `"${String(value)}"`;
}

/**
 * Generate reference path for a resolved object (e.g., Types.Weapons.Sword)
 */
export function generateReferencePath(
  refObject: any,
  referenceType: 'types' | 'tags',
  types?: any[],
  tags?: any[]
): string {
  const elementName = (refObject.name || refObject.id || 'Unknown').replace(/[^a-zA-Z0-9_]/g, '_');
  
  // Find the parent group for this element
  let groupName = 'Unknown';
  if (referenceType === 'types' && types) {
    for (const typeGroup of types) {
      if (typeGroup.data && Array.isArray(typeGroup.data)) {
        const found = typeGroup.data.find((el: any) => el.id === refObject.id);
        if (found) {
          groupName = (typeGroup.name || 'Unknown').replace(/[^a-zA-Z0-9_]/g, '_');
          break;
        }
      }
    }
  } else if (referenceType === 'tags' && tags) {
    for (const tagGroup of tags) {
      if (tagGroup.data && Array.isArray(tagGroup.data)) {
        const found = tagGroup.data.find((el: any) => el.id === refObject.id);
        if (found) {
          groupName = (tagGroup.name || 'Unknown').replace(/[^a-zA-Z0-9_]/g, '_');
          break;
        }
      }
    }
  }
  
  const className = referenceType === 'types' ? 'Types' : 'Tags';
  return `${className}.${groupName}.${elementName}`;
}
